import { fetchStaffThunk } from 'actions/staff/StaffActions';
import {
    bulkCompleteOpsTasksThunk,
    deleteOpsTaskThunk,
    fetchOpsTasksCountsThunk,
    fetchOpsTasksThunk,
    updateOpsTaskStatusThunk
} from 'actions/opsTasks/OpsTasksActions';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import { showErrorThunk } from '@spike/notifications-action';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { OpsTask, OpsTasksFilters, OpsTaskStatusFilter, OpsTaskStatusId, OpsTasksCounts } from 'model/OpsTask';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OpsTasksStatus } from 'reducers/opsTasks/OpsTasksState';
import { RootState } from 'store';
import { TasksDatePeriod } from '../TasksLayout';
import OpsTaskForm from './AddTask/OpsTaskForm';
import ConfirmCompleteAllModal from './ConfirmCompleteAllModal';
import OpsTasksHeader, { OpsTasksPendingCount } from './OpsTasksHeader';
import { OpsTaskStatusOption } from './OpsTaskStatusSelect';
import OpsTasksTable, { OpsTasksSortField } from './OpsTasksTable';
import OpsTasksPrintPreview from './Print/OpsTasksPrintPreview/OpsTasksPrintPreview';
import styles from './OpsTasks.module.scss';
import { nextColumnSort, TableColumnSort } from 'utils/tableSort';

type EmptyVariant = 'upcoming' | 'completed' | 'all';

const DEFAULT_SIZE = 25;

const FALLBACK_STATUS_OPTIONS: Array<OpsTaskStatusOption> = [
    { id: 1, name: 'To-Do' },
    { id: 2, name: 'In Progress' },
    { id: 3, name: 'Completed' }
];

const COMPLETE_ALL_TABS = ['todo', 'in_progress', 'past_due'];

const emptyVariant = (tabId: string): EmptyVariant => (tabId === 'completed' ? 'completed' : 'upcoming');

const tabToStatus = (tabId: string): OpsTaskStatusFilter | undefined => {
    switch (tabId) {
        case 'todo':
            return 'to_do';
        case 'in_progress':
            return 'in_progress';
        case 'past_due':
            return 'past_due';
        case 'completed':
            return 'completed';
        default:
            return undefined;
    }
};

interface OpsTasksProps {
    datePeriod: TasksDatePeriod;
    onChangeDate: (period: TasksDatePeriod) => void;
}

export const OpsTasks: FunctionComponent<OpsTasksProps> = ({ datePeriod, onChangeDate }) => {
    const dispatch = useDispatch();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const [search, setSearch] = useState('');
    const [selectedTab, setSelectedTab] = useState('todo');
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(DEFAULT_SIZE);
    const [taskToDelete, setTaskToDelete] = useState<OpsTask | undefined>(undefined);
    const [formOpen, setFormOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState<OpsTask | undefined>(undefined);
    const [showCompleteAllConfirm, setShowCompleteAllConfirm] = useState(false);
    const [previewTasks, setPreviewTasks] = useState<OpsTask[] | null>(null);

    const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Array<number>>([]);
    const [selectedRoomIds, setSelectedRoomIds] = useState<Array<number>>([]);
    const [selectedStaffIds, setSelectedStaffIds] = useState<Array<number>>([]);
    const [sort, setSort] = useState<TableColumnSort<OpsTasksSortField> | undefined>(undefined);

    const tasks = useSelector<RootState, Array<OpsTask>>(state => state.opsTasks.tasks);
    const counts = useSelector<RootState, OpsTasksCounts>(state => state.opsTasks.counts);
    const totalPages = useSelector<RootState, number>(state => state.opsTasks.totalPages);
    const status = useSelector<RootState, OpsTasksStatus>(state => state.opsTasks.status);
    const loading = useSelector<RootState, boolean>(state => state.opsTasks.loading);
    const masterStatuses = useSelector((state: RootState) => state.masterData.data.opsTaskStatuses);

    const showLocationColumn = organizationMarketplaces.length >= 2;

    const marketplaceIdsKey = organizationMarketplaces.map(marketplace => marketplace.id).join(',');
    const defaultMarketplaceIds = useMemo(
        () =>
            organizationMarketplaces.map(marketplace => marketplace.id).filter((id): id is number => id !== undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [marketplaceIdsKey]
    );

    const marketplaceIdsFilter = useMemo<Array<number> | undefined>(() => {
        if (organizationMarketplaces.length < 2) {
            return undefined;
        }
        return selectedMarketplaceIds.length > 0 ? selectedMarketplaceIds : defaultMarketplaceIds;
    }, [organizationMarketplaces.length, selectedMarketplaceIds, defaultMarketplaceIds]);

    const filters = useMemo<OpsTasksFilters>(
        () => ({
            status: tabToStatus(selectedTab),
            query: search,
            startDate: datePeriod.from,
            endDate: datePeriod.to,
            roomIds: selectedRoomIds,
            staffIds: selectedStaffIds,
            marketplaceIds: marketplaceIdsFilter,
            sortBy: sort?.field,
            sortOrder: sort?.direction
        }),
        [selectedTab, search, datePeriod, selectedRoomIds, selectedStaffIds, marketplaceIdsFilter, sort]
    );

    const statusOptions = useMemo<Array<OpsTaskStatusOption>>(() => {
        const fromMaster = (masterStatuses ?? [])
            .map(option => ({ id: option.id as OpsTaskStatusId, name: option.name }))
            .filter(option => option.id === 1 || option.id === 2 || option.id === 3);
        return fromMaster.length > 0 ? fromMaster : FALLBACK_STATUS_OPTIONS;
    }, [masterStatuses]);

    useEffect(() => {
        dispatch(fetchStaffThunk());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchOpsTasksThunk(filters, page, size));
        dispatch(fetchOpsTasksCountsThunk(filters));
    }, [dispatch, filters, page, size]);

    const handleChangeTab = (tabId: string) => {
        setPage(1);
        setSelectedTab(tabId);
    };

    const handleSearch = (value: string) => {
        setPage(1);
        setSearch(value);
    };

    const handleSizeChange = (newSize: number) => {
        setPage(1);
        setSize(newSize);
    };

    const handleChangeDate = (period: TasksDatePeriod) => {
        setPage(1);
        onChangeDate(period);
    };

    const handleChangeMarketplaces = (ids: Array<number>) => {
        setPage(1);
        setSelectedMarketplaceIds(ids);
    };

    const handleChangeRooms = (ids: Array<number>) => {
        setPage(1);
        setSelectedRoomIds(ids);
    };

    const handleChangeStaff = (ids: Array<number>) => {
        setPage(1);
        setSelectedStaffIds(ids);
    };

    const handleSort = (field: OpsTasksSortField) => {
        setPage(1);
        setSort(prev => nextColumnSort(field, prev));
    };

    const handleChangeStatus = (task: OpsTask, newStatus: OpsTaskStatusId) => {
        dispatch(updateOpsTaskStatusThunk(task.id, newStatus));
    };

    const handlePrint = () => {
        if (tasks.length === 0) {
            dispatch(showErrorThunk('There are no tasks to print.'));
            return;
        }
        setPreviewTasks(tasks);
    };

    const handlePrintClose = () => {
        setPreviewTasks(null);
    };

    const handleConfirmDelete = async () => {
        if (!taskToDelete) {
            return;
        }
        await dispatch(deleteOpsTaskThunk(taskToDelete.id));
        setTaskToDelete(undefined);
    };

    const handleCompleteAll = () => {
        setShowCompleteAllConfirm(false);
        const ids = tasks.filter(task => task.status !== 3).map(task => task.id);
        if (ids.length > 0) {
            dispatch(bulkCompleteOpsTasksThunk(ids));
        }
    };

    const handleAdd = () => {
        setTaskToEdit(undefined);
        setFormOpen(true);
    };

    const handleEdit = (task: OpsTask) => {
        setTaskToEdit(task);
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setTaskToEdit(undefined);
    };

    const pendingCount: OpsTasksPendingCount = {
        todoCount: counts.todo,
        inProgressCount: counts.inProgress,
        pastDueCount: counts.pastDue,
        completedCount: counts.completed,
        totalCount: counts.all
    };

    const showCompleteAll = COMPLETE_ALL_TABS.includes(selectedTab);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <OpsTasksHeader
                    search={search}
                    onSearch={handleSearch}
                    selectedTabId={selectedTab}
                    onChangeTab={handleChangeTab}
                    pendingCount={pendingCount}
                    datePeriod={datePeriod}
                    selectedMarketplaceIds={selectedMarketplaceIds}
                    selectedRoomIds={selectedRoomIds}
                    selectedStaffIds={selectedStaffIds}
                    onChangeDate={handleChangeDate}
                    onChangeMarketplaces={handleChangeMarketplaces}
                    onChangeRooms={handleChangeRooms}
                    onChangeStaff={handleChangeStaff}
                    onAdd={handleAdd}
                    onPrint={handlePrint}
                    showCompleteAll={showCompleteAll && tasks.length > 0}
                    onCompleteAllClick={() => setShowCompleteAllConfirm(true)}
                />
            </div>
            <div className={styles.body}>
                <OpsTasksTable
                    tasks={tasks}
                    statusOptions={statusOptions}
                    isLoading={loading}
                    showLocationColumn={showLocationColumn}
                    showCompleteAll={showCompleteAll}
                    emptyVariant={emptyVariant(selectedTab)}
                    sort={sort}
                    onSort={handleSort}
                    pagination={{ page, size, totalPages }}
                    onPageChange={setPage}
                    onSizeChange={handleSizeChange}
                    onEdit={handleEdit}
                    onDelete={setTaskToDelete}
                    onChangeStatus={handleChangeStatus}
                    onCompleteAllClick={() => setShowCompleteAllConfirm(true)}
                />
            </div>

            {formOpen && (
                <OpsTaskForm
                    taskToEdit={taskToEdit}
                    onClose={handleCloseForm}
                />
            )}

            {showCompleteAllConfirm && (
                <ConfirmCompleteAllModal
                    title="Complete All Visible Tasks"
                    question="Are you sure you want to mark all visible tasks as completed?"
                    labelYes="Yes, complete all"
                    onDiscard={() => setShowCompleteAllConfirm(false)}
                    onSave={handleCompleteAll}
                />
            )}

            {taskToDelete && (
                <GenericDeleteModal
                    className={styles.deleteModal}
                    title={<div className={styles.deleteModalTitle}>Delete Task</div>}
                    body={
                        <div className={styles.deleteModalBody}>
                            Are you sure you want to <strong>delete</strong> this task?
                        </div>
                    }
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setTaskToDelete(undefined)}
                    isLoading={status === OpsTasksStatus.Deleting}
                />
            )}

            {previewTasks && (
                <OpsTasksPrintPreview
                    tasks={previewTasks}
                    dateLabel={
                        datePeriod.from.isSame(datePeriod.to, 'day')
                            ? datePeriod.from.format('L')
                            : `${datePeriod.from.format('L')} – ${datePeriod.to.format('L')}`
                    }
                    onClose={handlePrintClose}
                />
            )}
        </div>
    );
};

export default OpsTasks;
